import { useState, useRef, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import type { CompanyRow } from '../contexts/EntityContext';

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function EntityTypeBadge({ type, isConsolidated }: { type: string; isConsolidated?: boolean }) {
  if (isConsolidated) {
    return (
      <span style={{
        fontSize: '10px',
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: '4px',
        background: 'rgba(139, 92, 246, 0.18)',
        color: '#a78bfa',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
      }}>All Locations</span>
    );
  }
  if (type === 'location') {
    return (
      <span style={{
        fontSize: '10px',
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: '4px',
        background: 'var(--nav-active-bg)',
        color: 'var(--cyan)',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
      }}>Location</span>
    );
  }
  return (
    <span style={{
      fontSize: '10px',
      fontWeight: 600,
      padding: '1px 6px',
      borderRadius: '4px',
      background: 'rgba(148, 163, 184, 0.12)',
      color: 'var(--text-muted)',
      letterSpacing: '0.3px',
      whiteSpace: 'nowrap',
    }}>Standalone</span>
  );
}

interface EntitySwitcherProps {
  onOpenChange?: (open: boolean) => void;
  sidebarExpanded?: boolean;
}

export default function EntitySwitcher({ onOpenChange, sidebarExpanded = false }: EntitySwitcherProps) {
  const { entityTree, activeCompanyId, activeOrgId, isConsolidatedView, switchToCompany, switchToOrg, hasMultipleEntities } = useEntity();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  function setOpenAndNotify(next: boolean) {
    if (next && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(next);
    onOpenChange?.(next);
  }

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setOpenAndNotify(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!hasMultipleEntities || !entityTree) return null;

  // Determine active entity display
  let activeName = 'Select entity';

  if (isConsolidatedView && activeOrgId) {
    const orgEntry = entityTree.organizations.find((o) => o.org.id === activeOrgId);
    activeName = orgEntry ? orgEntry.org.name : 'All Locations';
  } else if (activeCompanyId) {
    const standalone = entityTree.standalone.find((c) => c.id === activeCompanyId);
    if (standalone) {
      activeName = standalone.name;
    } else {
      for (const { locations } of entityTree.organizations) {
        const loc = locations.find((l) => l.id === activeCompanyId);
        if (loc) {
          activeName = loc.name;
          break;
        }
      }
    }
  }

  function isActive(id: string, isOrg = false): boolean {
    if (isOrg) return isConsolidatedView && activeOrgId === id;
    return !isConsolidatedView && activeCompanyId === id;
  }

  function DropdownItem({ company, isOrg = false, indented = false }: { company: CompanyRow; isOrg?: boolean; indented?: boolean }) {
    const active = isActive(company.id, isOrg);
    return (
      <button
        onClick={() => {
          if (isOrg) switchToOrg(company.id);
          else switchToCompany(company.id);
          setOpenAndNotify(false);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '8px 12px',
          paddingLeft: indented ? '28px' : '12px',
          background: active ? 'var(--nav-active-bg)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '8px',
          borderRadius: '6px',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--nav-hover-bg)';
        }}
        onMouseLeave={(e) => {
          if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent';
        }}
      >
        <span style={{
          flex: 1,
          fontSize: '13px',
          fontWeight: active ? 600 : 500,
          color: active ? 'var(--cyan)' : 'var(--text-body)',
          fontFamily: 'var(--font-sans)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {company.name}
        </span>
        <EntityTypeBadge type={company.entity_type} isConsolidated={isOrg} />
        {active && (
          <span style={{ color: 'var(--cyan)', flexShrink: 0 }}>
            <CheckIcon />
          </span>
        )}
      </button>
    );
  }

  // Entity initial for collapsed avatar
  const entityInitial = activeName.charAt(0).toUpperCase();

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: '8px' }}>
      <button
        ref={triggerRef}
        onClick={() => setOpenAndNotify(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: sidebarExpanded ? '6px 8px' : '4px',
          background: 'transparent',
          border: 'none',
          borderRadius: 10,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background 0.12s ease',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--nav-hover-bg)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {/* Avatar — styled as icon-only tile when collapsed, inline when expanded */}
        <span style={{
          width: 44,
          height: 44,
          minWidth: 44,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 10,
          // Show bordered tile only when collapsed
          border: sidebarExpanded ? 'none' : '1px solid var(--border-base)',
          background: sidebarExpanded ? 'var(--nav-active-bg)' : 'var(--bg-card-solid)',
          transition: 'border 0.12s ease, background 0.12s ease',
        }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 700,
            color: 'var(--cyan)',
            fontFamily: 'var(--font-sans)',
          }}>
            {entityInitial}
          </span>
        </span>

        {/* Name + chevron — only visible when sidebar is expanded */}
        {sidebarExpanded && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              gap: '6px',
              overflow: 'hidden',
              minWidth: 0,
              paddingLeft: 8,
            }}
          >
            <span style={{
              flex: 1,
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--text-body)',
              fontFamily: 'var(--font-sans)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {activeName}
            </span>
            <span style={{ color: '#A1A1AA', flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
              <ChevronDownIcon />
            </span>
          </span>
        )}
      </button>

      {/* Dropdown panel — rendered fixed so it escapes sidebar clip boundary */}
      {open && dropdownPos && (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: Math.max(dropdownPos.width, 220),
            minWidth: '220px',
            background: 'var(--bg-card-solid)',
            border: '1px solid var(--border-cyan-20)',
            borderRadius: 'var(--sol-radius-lg)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            zIndex: 9999,
            padding: '6px',
            maxHeight: '320px',
            overflowY: 'auto',
          }}
        >
          {/* Organizations */}
          {entityTree.organizations.map(({ org, locations }) => (
            <div key={org.id}>
              {/* Org header row — clicking switches to consolidated view */}
              <div style={{ marginBottom: '2px' }}>
                <DropdownItem company={org} isOrg />
              </div>
              {/* Location rows — indented */}
              {locations.map((loc) => (
                <div key={loc.id} style={{ marginBottom: '2px' }}>
                  <DropdownItem company={loc} indented />
                </div>
              ))}
              {entityTree.standalone.length > 0 && (
                <div style={{ borderTop: '1px solid var(--border-base)', margin: '6px 0' }} />
              )}
            </div>
          ))}

          {/* Standalone companies */}
          {entityTree.standalone.map((c) => (
            <div key={c.id} style={{ marginBottom: '2px' }}>
              <DropdownItem company={c} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
